import API from './api';
import { User } from './models';
import { getAuthCookie } from '../utils/cookies';
import NavigationController from '../core/navigation.controller';
import { companySetting, isFeatureEnabled } from '../company/session';
import { FEATURES } from '../company/features';

class DataController extends NavigationController {
    user = null;
    loading = true;

    get API() {
        return API.Instance();
    }

    async authenticate() {
        const token = getAuthCookie();

        if (token === 'expired' || !token) {
            User.resetInstance();
            return false;
        } else {
            const user = await this.API.requestUserByToken();
            if (!user) {
                User.resetInstance();
                return false;
            } else {
                // Keep the role permissions fresh (the instance survives page navigations).
                const instance = User.Instance(user);
                if (user.permissions) instance.permissions = user.permissions;
                return true;
            }
        }
    }

    async requestLogin(payload) {
        return await this.API.requestSignIn(payload);
    }

    /** Password recovery at the email's company (see API.requestPasswordStep). */
    async requestPasswordStep(step, company, payload) {
        return await this.API.requestPasswordStep(step, company, payload);
    }

    // -------- NOTIFICATIONS ----------
    async fetchNotifications(filters = {}) {
        return await this.API.requestNotifications(filters);
    }

    async fetchNotificationById(id) {
        if (!id) {
            console.warn('⚠️ fetchNotificationById called with undefined/null ID');
            return null;
        }
        return await this.API.requestNotificationById(id);
    }

    async fetchNotificationStats() {
        return await this.API.requestNotificationStats();
    }

    async fetchNotificationUnreadCount() {
        return await this.API.requestNotificationUnreadCount();
    }

    async markNotificationAsRead(id, userId) {
        return await this.API.requestMarkNotificationAsRead(id, userId);
    }

    async markMultipleNotificationsAsRead(notificationIds, userId) {
        return await this.API.requestMarkMultipleNotificationsAsRead(notificationIds, userId);
    }

    async dismissNotification(id, userId) {
        return await this.API.requestDismissNotification(id, userId);
    }

    async deleteNotification(id) {
        return await this.API.requestDeleteNotification(id);
    }

    async fetchClientById(id) {
        // ✅ Validate ID before making API call
        if (!id) {
            console.warn('⚠️ fetchClientById called with undefined/null ID');
            return null;
        }
        return await this.API.requestClientById(id);
    }

    async fetchUsers() {
        return await this.API.requestUsers();
    }

    async fetchClients() {
        return await this.API.requestClients();
    }

    async fetchUserById(id) {
        // ✅ Validate ID before making API call
        if (!id) {
            console.warn('⚠️ fetchUserById called with undefined/null ID');
            return null;
        }
        return await this.API.requestUserById(id);
    }

    async fetchUsersByUserType(userType) {
        return await this.API.requestUsersByUserType(userType);
    }

    /**
     * Fetch promotors with pre-calculated stats (optimized endpoint with caching)
     * @param {string} channel - Channel filter ('all', 'moderno', 'tradicional')
     * @param {boolean} forceRefresh - Force refresh cache
     * @param {string|null} clientId - Optional client ID to scope results
     * @returns {Promise<Object>} - { success, cached, data: { promotors, dateRange, computeTime } }
     */
    async fetchPromotorsStats(channel = 'all', forceRefresh = false, clientId = null) {
        return await this.API.requestPromotorsStats(channel, forceRefresh, clientId);
    }

    async updateUser(id, payload) {
        return await this.API.requestUpdateUserById(id, payload);
    }

    async updateClientById(id, payload) {
        return await this.API.requestUpdateClientById(id, payload);
    }

    async createClient(payload) {
        return await this.API.requestCreateClient(payload);
    }

    async bulkDeactivateStores(storeIds) {
        return await this.API.requestBulkDeactivateStores(storeIds);
    }

    async fetchStores(page = 1, limit = 1000, search = '', includePromotors = false) {
        return await this.API.requestStores(page, limit, search, includePromotors);
    }

    async fetchAllStores(includePromotors = false) {
        // ✅ For backward compatibility - fetches all stores
        return await this.API.requestAllStores(includePromotors);
    }

    /**
     * Fetch all stores with caching (optimized endpoint)
     * @param {boolean} forceRefresh - Force refresh the cache
     * @returns {Promise<Object>} - { success, cached, data: { stores, total, computeTime } }
     */
    async fetchStoresCached(forceRefresh = false) {
        return await this.API.requestStoresCached(forceRefresh);
    }

    async fetchStoresByClient(id) {
        return await this.API.requestStoresByClient(id);
    }

    async updateStoreById(id, payload) {
        return await this.API.requestUpdateStoreById(id, payload);
    }

    async fetchStoreChecksByClient(id, startDate, endDate) {
        return await this.API.requestStoreChecksByClient(id, startDate, endDate);
    }

    async fetchDashboardAnalytics(startDate, endDate, clientId = null) {
        return await this.API.requestDashboardAnalytics(startDate, endDate, clientId);
    }

    async fetchStoreById(id) {
        // ✅ Validate ID before making API call
        if (!id) {
            console.warn('⚠️ fetchStoreById called with undefined/null ID');
            return null;
        }
        return await this.API.requestStoreById(id);
    }

    async createStore(payload) {
        return await this.API.requestCreateStore(payload);
    }

    async fetchStoreTypeByValue(value) {
        return await this.API.requestStoreTypeByValue(value);
    }

    async fetchMonthlyStoreChecks(id, startDate, endDate) {
        // ✅ Validate ID before making API call
        if (!id) {
            console.warn('⚠️ fetchMonthlyStoreChecks called with undefined/null ID');
            return [];
        }
        return await this.API.requestMonthlyStoreChecks(id, startDate, endDate);
    }

    async fetchStoreCheckById(id) {
        // ✅ Validate ID before making API call
        if (!id) {
            console.warn('⚠️ fetchStoreCheckById called with undefined/null ID');
            return null;
        }
        return await this.API.requestStoreCheckById(id);
    }

    async fetchStoreChecksByStore(storeId, startDate, endDate) {
        // Fetch all store checks for a specific store
        if (!storeId) {
            console.warn('⚠️ fetchStoreChecksByStore called with undefined/null storeId');
            return null;
        }
        return await this.API.requestMonthlyStoreChecks(storeId, startDate, endDate);
    }

    async fetchPricesByType(storeType, date) {
        // ✅ Validate storeType before making API call
        if (!storeType) {
            console.warn('⚠️ fetchPricesByType called with undefined/null storeType');
            return { products: [] };
        }
        return await this.API.requestPricesByType(storeType, date);
    }

    async fetchStoreChecksByUser(id, startDate, endDate) {
        // ✅ Validate ID before making API call
        if (!id) {
            console.warn('⚠️ fetchStoreChecksByUser called with undefined/null ID');
            return [];
        }
        return await this.API.requestStoreChecksByUser(id, startDate, endDate);
    }

    async fetchFilteredStoreChecks(date, userId) {
        return await this.API.requestFilteredStoreChecks(date, userId);
    }

    async fetchAllStoreChecks() {
        return await this.API.requestAllStoreChecks();
    }

    async fetchPrices() {
        return await this.API.requestPrices();
    }

    // ------- PRODUCTS --------
    async fetchProducts(page = 1, limit = 50, category = null, search = null, includeInactive = false) {
        return await this.API.requestProducts(page, limit, category, search, includeInactive);
    }

    async fetchProductCategories() {
        return await this.API.requestProductCategories();
    }

    async createProduct(payload) {
        return await this.API.requestCreateProduct(payload);
    }

    async updateProductById(id, payload) {
        return await this.API.requestUpdateProductById(id, payload);
    }

    async deleteProductById(id) {
        return await this.API.requestDeleteProductById(id);
    }

    async bulkUpsertProducts(products) {
        return await this.API.requestProductsBulkUpsert(products);
    }

    async uploadProductImage(file) {
        return await this.API.requestUploadProductImage(file);
    }

    async createPrice(payload) {
        return await this.API.requestCreatePrice(payload);
    }

    // -------- PERMISSIONS (admin only) ----------
    async fetchPermissions() {
        return await this.API.requestPermissions();
    }

    async updatePermissions(role, payload) {
        return await this.API.requestUpdatePermissions(role, payload);
    }

    async resetPermissions(role) {
        return await this.API.requestResetPermissions(role);
    }

    // The store's own price list, or null when it uses its client's.
    async fetchStorePriceList(storeId) {
        const res = await this.API.requestStorePriceList(storeId);
        return res?._id ? res : null;
    }

    async saveStorePriceList(storeId, payload) {
        return await this.API.requestSaveStorePriceList(storeId, payload);
    }

    async deleteStorePriceList(storeId) {
        return await this.API.requestDeleteStorePriceList(storeId);
    }

    async fetchStorePriceListsByClient(clientId) {
        const res = await this.API.requestStorePriceListsByClient(clientId);
        return Array.isArray(res) ? res : [];
    }

    // Price list that applies to a store (its own, else its client's / store type's) as of `date`.
    async fetchPricesForStore(storeId, date) {
        if (!storeId) return { products: [] };
        if (!isFeatureEnabled(FEATURES.STORE_PRICE_RESOLVE)) return await this.fetchPricesForStoreByType(storeId, date);
        return await this.API.requestResolveStorePrice(storeId, date);
    }

    /**
     * For an API without `/price/resolve/store/:id`: the price list keyed by the store's type,
     * or by its client for the store types the company prices per client
     * (`config.modernStoreTypes`). Same `{ products }` shape as the resolver.
     */
    async fetchPricesForStoreByType(storeId, date) {
        const store = await this.fetchStoreById(storeId);
        if (!store || store.message) return { products: [] };
        const perClientTypes = companySetting('modernStoreTypes', []);
        const clientId = store.client?._id || store.client;
        const key = perClientTypes.includes(store.storeType) ? clientId : store.storeType;
        const response = await this.fetchPricesByType(key, date ? String(date).slice(0, 10) : date);
        return { products: Array.isArray(response?.products) ? response.products : [] };
    }

    async fetchStoreTypes() {
        return await this.API.requestStoreTypes();
    }

    async createStoreType(payload) {
        return await this.API.requestCreateStoreType(payload);
    }

    async fetchStoreTypeOverrides(value) {
        return await this.API.requestStoreTypeOverrides(value);
    }

    async fetchStoreTypeOverride(storeId) {
        return await this.API.requestStoreTypeOverride(storeId);
    }

    async saveStoreTypeOverride(storeId, payload) {
        return await this.API.requestSaveStoreTypeOverride(storeId, payload);
    }

    async deleteStoreTypeOverride(storeId) {
        return await this.API.requestDeleteStoreTypeOverride(storeId);
    }

    /** Same product list for several stores that currently share a segmentation. */
    async saveStoreTypeOverrides(storeIds, products) {
        return await this.API.requestSaveStoreTypeOverrides({ storeIds, products });
    }

    /** Several stores back to their client's segmentation. */
    async removeStoreTypeOverrides(storeIds) {
        return await this.API.requestRemoveStoreTypeOverrides({ storeIds });
    }

    async fetchInteractionSummary(date) {
        return await this.API.requestInteractionSummary(date);
    }

    async fetchPromoterRouteEfficiency(userId, date) {
        return await this.API.requestPromoterRouteEfficiency(userId, date);
    }

    async fetchStoreCheckRecency(startDate, endDate) {
        return await this.API.requestStoreCheckRecency(startDate, endDate);
    }

    async fetchCompetitorBrands(category) {
        return await this.API.requestCompetitorBrands(category);
    }

    async fetchCompetitorCategoryConfig() {
        return await this.API.requestCompetitorCategoryConfig();
    }

    async updateCompetitorCategoryConfig(payload) {
        return await this.API.requestUpdateCompetitorCategoryConfig(payload);
    }

    async fetchSegmentationTypes() {
        return await this.API.requestSegmentationTypes();
    }

    async createSegmentationType(payload) {
        return await this.API.requestCreateSegmentationType(payload);
    }

    async updateSegmentationType(id, payload) {
        return await this.API.requestUpdateSegmentationType(id, payload);
    }

    async fetchDistributorPriceVisuals(priceType, startDate, endDate, productId) {
        return await this.API.requestDistributorPriceVisuals(priceType, startDate, endDate, productId);
    }

    async fetchDistributorPriceAnomalyThreshold() {
        return await this.API.requestDistributorPriceAnomalyThreshold();
    }

    async updateDistributorPriceAnomalyThreshold(thresholdPercent) {
        return await this.API.requestUpdateDistributorPriceAnomalyThreshold({ thresholdPercent });
    }

    async reviewDistributorPriceObservation(payload) {
        return await this.API.requestReviewDistributorPriceObservation(payload);
    }

    async fetchLevantamientoGeneral(params = {}, signal) {
        return await this.API.requestLevantamientoGeneral(params, signal);
    }

    async fetchLevantamientoPriceBySku(params = {}, signal) {
        return await this.API.requestLevantamientoPriceBySku(params, signal);
    }

    async fetchLevantamientoPriceTrend(params = {}, signal) {
        return await this.API.requestLevantamientoPriceTrend(params, signal);
    }

    async fetchLevantamientoComparison(params = {}, signal) {
        return await this.API.requestLevantamientoComparison(params, signal);
    }

    async rebuildLevantamiento() {
        return await this.API.requestLevantamientoRebuild();
    }

    async fetchLevantamientoFacets() {
        return await this.API.requestLevantamientoFacets();
    }

    async fetchShelfUsage(params = {}, signal) {
        return await this.API.requestShelfUsage(params, signal);
    }

    async fetchShareOfShelf(params = {}, signal) {
        return await this.API.requestShareOfShelf(params, signal);
    }

    async fetchShareOfShelfStores(params = {}, signal) {
        return await this.API.requestShareOfShelfStores(params, signal);
    }

    async fetchNumericDistribution(params = {}, signal) {
        return await this.API.requestNumericDistribution(params, signal);
    }

    async fetchNumericDistributionStores(params = {}, signal) {
        return await this.API.requestNumericDistributionStores(params, signal);
    }

    async fetchNumericDistributionStoreProducts(params = {}, signal) {
        return await this.API.requestNumericDistributionStoreProducts(params, signal);
    }

    async fetchProductAvailability(params = {}, signal) {
        return await this.API.requestProductAvailability(params, signal);
    }

    async fetchAllProducts() {
        return await this.API.requestAllProducts();
    }

    async createUser(payload) {
        return await this.API.requestCreateUser(payload);
    }

    async fetchCalendarByUser(id) {
        return await this.API.requestCalendarByUser(id);
    }

    async createCalendar(payload) {
        return await this.API.requestCreateCalendar(payload);
    }

    async fetchPolls() {
        return await this.API.requestPolls();
    }

    async deletePoll(id) {
        return await this.API.requestDeletePoll(id);
    }

    async updateStoreAuditor(payload) {
        return await this.API.requestUpdateStoreAuditor(payload);
    }

    async fetchCampaigns() {
        return await this.API.requestTradeCampaigns();
    }

    async createCampaign(payload) {
        return await this.API.requestCreateTradeCampaign(payload);
    }

    async updateCampaign(campaignId, payload) {
        return await this.API.requestUpdateTradeCampaign(campaignId, payload);
    }

    async fetchContracts() {
        return await this.API.requestTradeContracts();
    }

    async createContract(payload) {
        return await this.API.requestCreateTradeContract(payload);
    }

    async updateContract(contractId, payload) {
        return await this.API.requestUpdateTradeContract(contractId, payload);
    }

    async fetchContractById(id) {
        return await this.API.requestTradeContractById(id);
    }

    async updateContractStorePrice(contractId, storeId, price) {
        return await this.API.requestUpdateTradeContractStorePrice(contractId, storeId, price);
    }

    async updateCampaignStorePrice(campaignId, storeId, price) {
        return await this.API.requestUpdateTradeCampaignStorePrice(campaignId, storeId, price);
    }

    async fetchPromotorAnalytics(period = 'week', limit = 10, channel = 'all', tagId = null, startDate = null, endDate = null) {
        return await this.API.requestPromotorAnalytics(period, limit, channel, tagId, startDate, endDate);
    }

    async fetchPromotorPerformance(promotorId, days = 30) {
        return await this.API.requestPromotorPerformance(promotorId, days);
    }

    async fetchStoreCheckByContractId(id) {
        return await this.API.requestStoreCheckByContractId(id);
    }

    async fetchCampaignById(id) {
        return await this.API.requestTradeCampaignById(id);
    }

    async fetchContractsByStore(storeId) {
        return await this.API.requestContractsByStore(storeId);
    }

    async fetchContractsByClient(clientId) {
        return await this.API.requestContractsByClient(clientId);
    }

    async fetchCampaignsByStore(storeId) {
        return await this.API.requestCampaignsByStore(storeId);
    }

    async fetchCampaignsByClient(clientId) {
        return await this.API.requestCampaignsByClient(clientId);
    }

    // -------- TAGS --------
    async fetchTags(filters) {
        return await this.API.requestTags(filters);
    }

    async requestTags(filters) {
        return await this.API.requestTags(filters);
    }

    async requestTagById(tagId) {
        return await this.API.requestTagById(tagId);
    }

    async requestCreateTag(payload) {
        return await this.API.requestCreateTag(payload);
    }

    async requestUpdateTag(tagId, payload) {
        return await this.API.requestUpdateTag(tagId, payload);
    }

    async requestDeleteTag(tagId) {
        return await this.API.requestDeleteTag(tagId);
    }

    async requestAssignTag(tagId, payload) {
        return await this.API.requestAssignTag(tagId, payload);
    }

    async requestRemoveTag(tagId, payload) {
        return await this.API.requestRemoveTag(tagId, payload);
    }

    async requestTagItems(tagId, filters) {
        return await this.API.requestTagItems(tagId, filters);
    }

    async requestItemTags(modelType, modelId) {
        return await this.API.requestItemTags(modelType, modelId);
    }

    async requestSOSAnalysis(tagId) {
        return await this.API.requestSOSAnalysis(tagId);
    }

    // -------- ACTIVITY LOGS --------
    async fetchActivityLogs(filters = {}) {
        return await this.API.requestActivityLogs(filters);
    }

    async fetchActivityLogById(id) {
        if (!id) {
            console.warn('⚠️ fetchActivityLogById called with undefined/null ID');
            return null;
        }
        return await this.API.requestActivityLogById(id);
    }

    async fetchActivityLogsStats(filters = {}) {
        return await this.API.requestActivityLogsStats(filters);
    }

    async fetchActivityLogsDeactivations(filters = {}) {
        return await this.API.requestActivityLogsDeactivations(filters);
    }

    async fetchActivityLogsByEntity(entityType, entityId, filters = {}) {
        if (!entityType || !entityId) {
            console.warn('⚠️ fetchActivityLogsByEntity called with undefined/null entityType or entityId');
            return null;
        }
        return await this.API.requestActivityLogsByEntity(entityType, entityId, filters);
    }

    async fetchActivityLogsByUser(userId, filters = {}) {
        if (!userId) {
            console.warn('⚠️ fetchActivityLogsByUser called with undefined/null userId');
            return null;
        }
        return await this.API.requestActivityLogsByUser(userId, filters);
    }

    async fetchAuditFieldOptions() {
        return await this.API.requestAuditFieldOptions();
    }

    async updateAuditFieldOptions(payload) {
        return await this.API.requestUpdateAuditFieldOptions(payload);
    }

    async fetchAuditAdminAnalytics() {
        return await this.API.requestAuditAdminAnalytics();
    }

    async fetchAuditAdminAuditProducts(params = {}) {
        return await this.API.requestAuditAdminAuditProducts(params);
    }

    async fetchAuditAdminAuditProductFacets() {
        return await this.API.requestAuditAdminAuditProductFacets();
    }

    async fetchAuditAdminAuditProductById(id) {
        return await this.API.requestAuditAdminAuditProductById(id);
    }

    async updateAuditAdminAuditProduct(id, payload) {
        return await this.API.requestUpdateAuditAdminAuditProduct(id, payload);
    }

    async fetchAuditAdminAuditRecords(params = {}) {
        return await this.API.requestAuditAdminAuditRecords(params);
    }

    async fetchAuditAdminStoreChecks(params = {}) {
        return await this.API.requestAuditAdminStoreChecks(params);
    }

    async fetchAuditAdminAuditRecordById(id) {
        return await this.API.requestAuditAdminAuditRecordById(id);
    }

    async fetchAuditAdminStoreCheckRecords(id) {
        return await this.API.requestAuditAdminStoreCheckRecords(id);
    }

    async fetchAuditAdminProductDuplicates(params = {}) {
        return await this.API.requestAuditAdminProductDuplicates(params);
    }

    async mergeAuditAdminProducts(payload) {
        return await this.API.requestAuditAdminProductMerge(payload);
    }

    async bulkUpdateAuditAdminProducts(payload) {
        return await this.API.requestAuditAdminProductsBulk(payload);
    }

    async bulkUpdateAuditAdminRecords(payload) {
        return await this.API.requestAuditAdminRecordsBulk(payload);
    }

    async fetchAuditAdminReviewQueue(params = {}) {
        return await this.API.requestAuditAdminReviewQueue(params);
    }

    async fetchAuditAdminAuditorStats(userId, params = {}) {
        return await this.API.requestAuditAdminAuditorStats(userId, params);
    }

    async fetchAuditAdminAuditorActivity(userId, params = {}) {
        return await this.API.requestAuditAdminAuditorActivity(userId, params);
    }

    async deleteAuditAdminAuditRecord(id) {
        return await this.API.requestDeleteAuditAdminAuditRecord(id);
    }

    async patchAuditAdminAuditRecordCurrency(id, payload) {
        return await this.API.requestPatchAuditAdminAuditRecordCurrency(id, payload);
    }

    async bulkDeleteAuditAdminAuditRecords(ids) {
        return await this.API.requestBulkDeleteAuditAdminAuditRecords({ ids });
    }

    async bulkCurrencyAuditAdminAuditRecords(ids, currency) {
        return await this.API.requestBulkCurrencyAuditAdminAuditRecords({ ids, currency });
    }

    async deleteAuditAdminStoreCheck(id) {
        return await this.API.requestDeleteAuditAdminStoreCheck(id);
    }

    async deleteAuditAdminAuditProduct(id, params = {}) {
        return await this.API.requestDeleteAuditAdminAuditProduct(id, params);
    }
}

export default DataController;
